import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = 'raith-theme-mode';

// Detect system preference
const getSystemPreference = (): boolean => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const isDarkPreferred = window.matchMedia('(prefers-color-scheme: dark)').matches;
    console.log('System preference detected:', isDarkPreferred ? 'dark' : 'light');
    return isDarkPreferred;
  }
  console.log('System preference detection not available, defaulting to light');
  return false;
};

// Get stored theme or default based on system preference
const getStoredTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && ['light', 'dark'].includes(stored)) {
      return stored as ThemeMode;
    }
    // If stored was 'system' or invalid, use system preference to decide
    return getSystemPreference() ? 'dark' : 'light';
  }
  return 'light';
};

// Calculate if dark theme should be active
const calculateIsDark = (mode: ThemeMode): boolean => {
  return mode === 'dark';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredTheme);
  const [isDark, setIsDark] = useState<boolean>(() => calculateIsDark(getStoredTheme()));

  // Update theme when mode changes
  useEffect(() => {
    const newIsDark = calculateIsDark(mode);
    setIsDark(newIsDark);
    
    // Apply theme to document
    const root = document.documentElement;
    
    // Add transition class to prevent flashing
    root.classList.add('theme-switching');
    
    // Set data attribute for CSS
    root.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
    
    // Remove transition class after a short delay
    setTimeout(() => {
      root.classList.remove('theme-switching');
    }, 100);
    
    // Store preference
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);


  // Initialize theme on mount
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  const toggleTheme = () => {
    setMode(isDark ? 'light' : 'dark');
  };

  const value: ThemeContextType = {
    mode,
    isDark,
    setMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
